import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { centralApi, type PlatformConfigSource } from '@marque/api/central'

// AB#3770 — the platform Azure Document Intelligence account behind handwriting transcription of
// CRM attachments (AB#3769). Every tenant is granted it by default (Overview tab toggle); a tenant
// that registers its own azure-document-intelligence connection uses that instead. Terraform
// provisions the account and seeds the endpoint and key into central-api's configuration, so a
// fresh environment works with nothing typed here; an operator value entered on this card
// overrides the seed, and saving a blank returns to it. The key is write-only: stored via Key
// Vault, never shown back.
const field = 'w-full rounded-md border border-neutral-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'

function sourceLabel(source: PlatformConfigSource): string {
    switch (source) {
        case 'operator': return 'set here'
        case 'deployment': return 'from the deployment (Terraform)'
        default: return 'not configured'
    }
}

export function DocumentIntelligenceKeysCard() {
    const qc = useQueryClient()
    const [endpointEdited, setEndpointEdited] = useState<string | null>(null)
    const [keyEdited, setKeyEdited] = useState<string | null>(null)

    const { data, isLoading } = useQuery({
        queryKey: ['document-intelligence-config'],
        queryFn: centralApi.getDocumentIntelligenceConfig,
    })

    const save = useMutation({
        mutationFn: () => centralApi.setDocumentIntelligenceConfig({
            ...(endpointEdited !== null ? { endpoint: endpointEdited.trim() } : {}),
            ...(keyEdited !== null ? { apiKey: keyEdited.trim() } : {}),
        }),
        onSuccess: () => {
            setEndpointEdited(null)
            setKeyEdited(null)
            void qc.invalidateQueries({ queryKey: ['document-intelligence-config'] })
        },
    })

    const dirty = endpointEdited !== null || keyEdited !== null
    const configured = !!data?.endpoint && data.hasApiKey

    return (
        <div className="bg-white rounded-lg border border-neutral-200 p-6 mt-6" data-testid="document-intelligence-config">
            <h2 className="text-lg font-semibold text-neutral-900">Handwriting recognition — Platform Azure Document Intelligence</h2>
            <p className="mt-1 text-sm text-neutral-500">
                Transcribes photographed notes attached to CRM records. Tenants are granted this account by default
                (revoke per tenant on its Overview tab); a tenant with its own azure-document-intelligence connection
                uses that instead. Terraform seeds both values from Key Vault — enter values here only to override
                the deployment, and save a blank to go back to it. The key never leaves the server.
            </p>
            <p className={`mt-2 text-xs ${configured ? 'text-emerald-600' : 'text-amber-600'}`}>
                {isLoading ? 'Loading…' : configured ? 'Configured — tenants can transcribe.' : 'Not configured — tenants without their own connection see no Transcribe button.'}
            </p>
            <div className="mt-4">
                <label className="block text-sm font-medium text-neutral-700 mb-1">Endpoint</label>
                <input
                    type="text"
                    value={endpointEdited ?? data?.endpoint ?? ''}
                    onChange={e => setEndpointEdited(e.target.value)}
                    placeholder="https://<resource>.cognitiveservices.azure.com"
                    autoComplete="off"
                    className={field}
                    disabled={isLoading}
                />
                {data && endpointEdited === null && (
                    <p className="mt-1 text-xs text-neutral-500">Endpoint {sourceLabel(data.endpointSource)}.</p>
                )}
            </div>
            <div className="mt-4">
                <label className="block text-sm font-medium text-neutral-700 mb-1">API key</label>
                <input
                    type="password"
                    value={keyEdited ?? ''}
                    onChange={e => setKeyEdited(e.target.value)}
                    placeholder={data?.hasApiKey ? 'Enter a new key to replace, save empty to clear' : 'Ocp-Apim-Subscription-Key value'}
                    autoComplete="off"
                    className={field}
                    disabled={isLoading}
                />
                {data && keyEdited === null && (
                    <p className={`mt-1 text-xs ${data.hasApiKey ? 'text-emerald-600' : 'text-neutral-500'}`}>
                        {data.hasApiKey ? `A key is configured (${sourceLabel(data.apiKeySource)}) — the value is never shown back.` : 'No key configured.'}
                    </p>
                )}
            </div>
            <div className="mt-4 flex items-center gap-3">
                <button
                    type="button"
                    disabled={!dirty || save.isPending}
                    onClick={() => save.mutate()}
                    className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
                >
                    {save.isPending ? 'Saving…' : 'Save'}
                </button>
                {save.isSuccess && !dirty && <span className="text-sm text-emerald-600">Saved.</span>}
                {save.isError && <span className="text-sm text-red-600">Failed to save.</span>}
            </div>
        </div>
    )
}
